import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useMsal } from "@azure/msal-react";

import {
    Building2,
    Check,
    ChevronRight,
    Maximize2,
    Minimize2,
    Pencil,
    Plus,
    X
} from "lucide-react";

import { getToken, useMsalAuth } from "../../authConfig";
import { LoginContext } from "../../loginContext";
import {
    getCompanyOrgStructureApi,
    updateCompanyOrgStructureApi
} from "../../api/orgStructureApi";
import { OrgRoleManager } from "./OrgRoleManager";
import { OrgStructureCanvas } from "./OrgStructureCanvas";
import { OrgStructureNodeDialog } from "./OrgStructureNodeDialog";
import styles from "./CompanyOrgStructure.module.css";
import {
    addNode,
    cloneDocument,
    deleteNode,
    documentsEqual
} from "./orgStructureMutations";
import { validateDocument } from "./orgStructureSchema";
import {
    CompanyOrgStructureDocument,
    EMPTY_ORG_STRUCTURE_DOCUMENT,
    ORG_NODE_KINDS,
    OrgNodeKind
} from "./orgStructureTypes";

function formatUpdatedAt(timestamp: number | undefined, locale: string): string {
    if (!timestamp) {
        return "";
    }
    return new Date(timestamp * 1000).toLocaleString(locale);
}

interface OrgStructureEditorProps {
    onNodeDialogOpenChange?: (open: boolean) => void;
}

export function OrgStructureEditor({ onNodeDialogOpenChange }: OrgStructureEditorProps = {}) {
    const { t, i18n } = useTranslation();
    const { loggedIn } = useContext(LoginContext);
    const client = useMsalAuth ? useMsal().instance : undefined;

    const [saved, setSaved] = useState<CompanyOrgStructureDocument | null>(null);
    const [draft, setDraft] = useState<CompanyOrgStructureDocument>(
        cloneDocument(EMPTY_ORG_STRUCTURE_DOCUMENT)
    );
    const [editing, setEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [fullscreenOpen, setFullscreenOpen] = useState(false);

    const isNodeDialogOpen = dialogOpen && editing && !!selectedNodeId;

    useEffect(() => {
        onNodeDialogOpenChange?.(isNodeDialogOpen);
    }, [isNodeDialogOpen, onNodeDialogOpenChange]);

    useEffect(() => {
        return () => onNodeDialogOpenChange?.(false);
    }, [onNodeDialogOpenChange]);

    const isDirty = useMemo(() => {
        if (!saved) {
            return !documentsEqual(draft, EMPTY_ORG_STRUCTURE_DOCUMENT);
        }
        return !documentsEqual(draft, saved);
    }, [draft, saved]);

    const getIdToken = useCallback(async () => {
        if (client && loggedIn) {
            try {
                return await getToken(client);
            } catch (err) {
                console.error("Failed to get auth token:", err);
                return undefined;
            }
        }
        return undefined;
    }, [client, loggedIn]);

    const loadDocument = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const idToken = await getIdToken();
            const fetched = await getCompanyOrgStructureApi(idToken);
            setSaved(fetched);
            setDraft(cloneDocument(fetched));
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : t("settingsPage.errors.loadFailed");
            setError(message);
        } finally {
            setLoading(false);
        }
    }, [getIdToken, t]);

    useEffect(() => {
        void loadDocument();
    }, [loadDocument]);

    useEffect(() => {
        if (!fullscreenOpen) {
            return;
        }
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setFullscreenOpen(false);
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [fullscreenOpen]);

    const startEditing = () => {
        setDraft(cloneDocument(saved ?? EMPTY_ORG_STRUCTURE_DOCUMENT));
        setError(null);
        setEditing(true);
    };

    const cancelEditing = () => {
        setDraft(cloneDocument(saved ?? EMPTY_ORG_STRUCTURE_DOCUMENT));
        setSelectedNodeId(null);
        setDialogOpen(false);
        setFullscreenOpen(false);
        setError(null);
        setEditing(false);
    };

    const saveDocument = async () => {
        const validationError = validateDocument(draft);
        if (validationError) {
            setError(validationError);
            return;
        }

        try {
            setSaving(true);
            setError(null);
            const idToken = await getIdToken();
            const updated = await updateCompanyOrgStructureApi(draft, idToken);
            setSaved(updated);
            setDraft(cloneDocument(updated));
            setEditing(false);
            setSelectedNodeId(null);
            setDialogOpen(false);
            setFullscreenOpen(false);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : t("settingsPage.errors.saveFailed");
            setError(message);
        } finally {
            setSaving(false);
        }
    };

    const handleAddNode = useCallback(
        (kind: OrgNodeKind) => {
            const parentId = selectedNodeId ?? undefined;
            const { document: next, node } = addNode(draft, kind, parentId);
            setDraft(next);
            setSelectedNodeId(node.id);
            setDialogOpen(true);
        },
        [draft, selectedNodeId]
    );

    const handleNodeSelect = (nodeId: string) => {
        setSelectedNodeId(nodeId);
        if (editing) {
            setDialogOpen(true);
        }
    };

    const handleDialogSave = (document: CompanyOrgStructureDocument) => {
        setDraft(document);
    };

    const handleDeleteNode = () => {
        if (!selectedNodeId) {
            return;
        }
        setDraft(deleteNode(draft, selectedNodeId));
        setSelectedNodeId(null);
    };

    const handlePersonClick = (_personId: string) => {
        // Reserved for future person profile dialog (phase 2+).
    };

    const activeDocument = editing ? draft : saved ?? EMPTY_ORG_STRUCTURE_DOCUMENT;
    const nodeCount = activeDocument.nodes.length;
    const updatedAt = formatUpdatedAt((saved ?? draft).updated_at, i18n.language);
    const updatedBy = (saved ?? draft).updated_by;

    const toolbar = editing ? (
        <div className={styles.toolbar}>
            {ORG_NODE_KINDS.map(kind => (
                <button
                    key={kind}
                    type="button"
                    className={styles.toolbarButton}
                    onClick={() => handleAddNode(kind)}
                >
                    <Plus size={14} />
                    {t(`settingsPage.company.orgStructure.toolbar.add_${kind}`)}
                </button>
            ))}
        </div>
    ) : null;

    const canvasProps = {
        document: activeDocument,
        selectedNodeId,
        onNodeSelect: handleNodeSelect,
        readOnly: !editing
    };

    return (
        <div className={styles.section}>
            <details className={styles.disclosure}>
                <summary className={styles.summary}>
                    <ChevronRight size={16} className={styles.disclosureChevron} />
                    <Building2 size={18} />
                    <span className={styles.summaryTitle}>
                        {t("settingsPage.company.orgStructure.title")}
                    </span>
                    <span className={styles.summaryMeta}>
                        {t("settingsPage.company.orgStructure.nodeCount", { count: nodeCount })}
                    </span>
                </summary>

                <div className={styles.body}>
                    <p className={styles.description}>{t("settingsPage.company.orgStructure.description")}</p>

                    <div className={styles.actionsRow}>
                        {!editing ? (
                            <button
                                type="button"
                                className={styles.editButton}
                                onClick={startEditing}
                                disabled={loading}
                            >
                                <Pencil size={16} />
                                {t("settingsPage.account.edit")}
                            </button>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    className={styles.cancelButton}
                                    onClick={cancelEditing}
                                    disabled={saving}
                                >
                                    <X size={16} />
                                    {t("settingsPage.account.cancel")}
                                </button>
                                <button
                                    type="button"
                                    className={styles.saveButton}
                                    onClick={() => void saveDocument()}
                                    disabled={saving || !isDirty}
                                >
                                    <Check size={16} />
                                    {saving ? t("settingsPage.account.saving") : t("settingsPage.account.save")}
                                </button>
                            </>
                        )}
                    </div>

                    {editing ? <OrgRoleManager document={draft} onChange={setDraft} /> : null}

                    {toolbar}

                    {error ? <p className={styles.errorText}>{error}</p> : null}
                    {loading ? <p className={styles.emptyState}>{t("settingsPage.loading")}</p> : null}

                    {!loading && nodeCount === 0 ? (
                        <p className={styles.emptyState}>{t("settingsPage.company.orgStructure.emptyState")}</p>
                    ) : null}

                    {!loading && nodeCount > 0 ? (
                        <div className={styles.canvasWrapper}>
                            <button
                                type="button"
                                className={styles.canvasExpandButton}
                                onClick={() => setFullscreenOpen(true)}
                                aria-label={t("settingsPage.company.orgStructure.expandCanvas")}
                            >
                                <Maximize2 size={16} />
                            </button>
                            <OrgStructureCanvas {...canvasProps} variant="inline" />
                        </div>
                    ) : null}

                    {updatedAt ? (
                        <p className={styles.metaText}>
                            {t("settingsPage.company.orgStructure.lastUpdated", {
                                who: updatedBy || t("settingsPage.company.unknownEditor"),
                                when: updatedAt
                            })}
                        </p>
                    ) : (
                        <p className={styles.metaText}>
                            {t("settingsPage.company.orgStructure.neverUpdated")}
                        </p>
                    )}
                </div>
            </details>

            {fullscreenOpen ? (
                <div className={styles.fullscreenOverlay} role="dialog" aria-modal="true">
                    <div className={styles.fullscreenPanel}>
                        <div className={styles.fullscreenHeader}>
                            <span className={styles.fullscreenTitle}>
                                {t("settingsPage.company.orgStructure.title")}
                            </span>
                            <button
                                type="button"
                                className={styles.fullscreenCloseButton}
                                onClick={() => setFullscreenOpen(false)}
                                aria-label={t("settingsPage.company.orgStructure.collapseCanvas")}
                            >
                                <Minimize2 size={18} />
                            </button>
                        </div>
                        {editing ? <OrgRoleManager document={draft} onChange={setDraft} /> : null}
                        {toolbar}
                        <div className={styles.fullscreenBody}>
                            <OrgStructureCanvas {...canvasProps} variant="fullscreen" />
                        </div>
                    </div>
                </div>
            ) : null}

            <OrgStructureNodeDialog
                open={isNodeDialogOpen}
                nodeId={selectedNodeId}
                document={draft}
                onOpenChange={setDialogOpen}
                onSave={handleDialogSave}
                onDelete={handleDeleteNode}
                onPersonClick={handlePersonClick}
            />
        </div>
    );
}
